import { Bold, Italic, Link2, List } from 'lucide-react';

// Formatting toolbar for admin textareas. Inserts the safe inline syntax
// (**bold**, *italic*, "- " list items, [text](url)) at the cursor/selection
// using the native setter trick so React-controlled state updates.
const applyEdit = (ta, transform) => {
  const { selectionStart: start, selectionEnd: end, value } = ta;
  const { next, cursorStart, cursorEnd } = transform(value, start, end);
  const setter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set;
  setter.call(ta, next);
  ta.dispatchEvent(new Event('input', { bubbles: true }));
  requestAnimationFrame(() => {
    ta.focus();
    ta.setSelectionRange(cursorStart, cursorEnd);
  });
};

const wrapSelection = (before, after, placeholder) => (value, start, end) => {
  const selected = value.slice(start, end) || placeholder;
  const next = value.slice(0, start) + before + selected + after + value.slice(end);
  return { next, cursorStart: start + before.length, cursorEnd: start + before.length + selected.length };
};

const prefixLines = (value, start, end) => {
  const selected = value.slice(start, end) || 'List item';
  const lines = selected.split('\n').map((l) => (l.trim().startsWith('- ') ? l : `- ${l.replace(/^-\s+/, '')}`));
  const inserted = lines.join('\n');
  return { next: value.slice(0, start) + inserted + value.slice(end), cursorStart: start, cursorEnd: start + inserted.length };
};

const insertLink = (value, start, end) => {
  const selected = value.slice(start, end) || 'link text';
  const inserted = `[${selected}](https://)`;
  const cursor = start + selected.length + 3; // inside the URL parens
  return { next: value.slice(0, start) + inserted + value.slice(end), cursorStart: cursor, cursorEnd: cursor + 8 };
};

export const FormatToolbar = ({ targetRef, id }) => {
  const run = (transform) => {
    const ta = targetRef.current;
    if (ta) applyEdit(ta, transform);
  };
  const btn = 'inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-1 text-[11px] font-bold text-navy-900 transition-colors hover:bg-navy-50';
  return (
    <div className="mb-1.5 flex items-center gap-1.5" role="toolbar" aria-label="Text formatting" data-testid={`${id}-toolbar`}>
      <button type="button" className={btn} data-testid={`${id}-bold`} title="Bold" onClick={() => run(wrapSelection('**', '**', 'bold text'))}>
        <Bold className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <button type="button" className={btn} data-testid={`${id}-italic`} title="Italic" onClick={() => run(wrapSelection('*', '*', 'italic text'))}>
        <Italic className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <button type="button" className={btn} data-testid={`${id}-list`} title="Bullet list" onClick={() => run(prefixLines)}>
        <List className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
      <button type="button" className={btn} data-testid={`${id}-link`} title="Link" onClick={() => run(insertLink)}>
        <Link2 className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  );
};
